// libs/extensions/src/lib/plugins/jeppesen/aviation/plugins/navigation/selected-item/selected-item.service.ts

import { TitleCasePipe } from '@angular/common';
import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Point from '@arcgis/core/geometry/Point';
import { NGXLogger } from 'ngx-logger';
import {
  catchError,
  defaultIfEmpty,
  EMPTY,
  from,
  Observable,
  of,
  switchMap,
  take
} from 'rxjs';
import { ToolkitApiService } from '@jeppesen-foreflight/jmkw-toolkit';
import {
  JepGraphic,
  JepLayer,
  JepMapViewHitTestOptions,
  JepViewClickEvent
} from '@jeppesen-foreflight/jmkw-types';
import { PluginIDs } from '../../../../../plugin-ids.type';
import { NavigationFeatureType } from '../typings/navigation-feature-type';
import { FeatureDetail } from '../typings/features/feature-detail';
import { FeatureHighlightService } from './services/feature-highlight.service';
import { NavigationHitTestService } from './services/navigation-hit-test.service';
import { ManualFeatureSelectionProperties } from './typings/manual-feature-selection-properties';
import { NavWidgetComponent } from './widgets/nav-widget.component';
import type { ClickEvent } from "@arcgis/core/views/input/types";
import type { ViewHitTestResult } from "@arcgis/core/views/types";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

@Injectable({
  providedIn: 'root'
})
export class SelectedItemService implements OnDestroy {
  id: PluginIDs = 'selected-item';
  private readonly _logger = inject(NGXLogger);
  private readonly _api = inject(ToolkitApiService);
  private readonly _navHitTestSvc = inject(NavigationHitTestService);
  private readonly _featureHighlightSvc = inject(FeatureHighlightService);
  private _clickHandle!: ResourceHandle;

  currentSelection = signal<FeatureDetail | null>(null);

  /**
   * Updates the current selection and manages the highlighting of the selected feature.
   *
   * @param selection - The feature detail to be set as the current selection, or null to clear the selection.
   */
  updateSelection(selection: FeatureDetail | null) {
    this.currentSelection.set(selection);
    this._logger.trace('[SelectedItemService] New selected item:', selection);
    if (selection) {
      this._addFeatureDetailWidget(selection);
      this._featureHighlightSvc.addSelectionHighlight(
        selection.graphic as JepGraphic
      );
    } else {
      this._featureHighlightSvc.removeSelectionHighlight();
    }
  }

  /**
   * Updates the click handler for the specified layer, removing any existing click handler.
   *
   * @param layer - The layer to which the click handler will be added.
   */
  updateClickHandler(layer: JepLayer) {
    if (this._clickHandle) {
      this._clickHandle.remove();
    }
    this._addClickHandler(layer);
  }

  /**
   * Adds a click handler to the specified layer that processes click events and updates the selection.
   *
   * @param layer - The layer to which the click handler will be added.
   */
  private _addClickHandler(layer: JepLayer) {
    const view = this._api.getMapView();
    this._clickHandle = view.on('click', (event: JepViewClickEvent) => {
      // only include graphics from navLayer in the hitTest
      const opts: JepMapViewHitTestOptions = {
        include: layer
      };

      this.clearExistingSelection();
      view
        .hitTest(event, opts)
        .then((response: ViewHitTestResult) => {
          const sub = this._navHitTestSvc
            .clickResultHandler(response, event)
            .pipe(take(1))
            .subscribe((result: FeatureDetail) => {
              if (sub) {
                sub.unsubscribe();
              }
              this.updateSelection(result);
            });
        })
        .catch((error) => {
          if (error.name != 'AbortError') {
            this._logger.error(error);
          }
        });
    });
  }

  /**
   * Programmatically selects a feature using the provided properties and hitTest options.
   *
   * @param properties - The properties containing latitude, longitude, attribute name/value, and optional zoom level.
   * @param options - A set of options that allow for including or excluding specific layers and/or graphics
   * to the hitTest results
   *
   * @see https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#hitTest
   */

  selectFeature<T extends NavigationFeatureType>(
    properties: ManualFeatureSelectionProperties<T>,
    options: JepMapViewHitTestOptions = {
      include: this._api.getLayerById('navigation') || []
    }
  ) {
    const { latitude, longitude } = properties;

    this.clearExistingSelection();

    const view = this._api.getMapView();
    const mapPoint = new Point({
      latitude,
      longitude,
      spatialReference: view.spatialReference
    });

    this._api.mapReady$
      .pipe(
        // 1) call goTo() for side-effect, but don’t wrap in `from()`
        switchMap(() => {
          view.goTo(mapPoint);
          return of(undefined);
        }),
        // 2) even if _waitForLayerViewUpdate returns EMPTY, force at least one emission
        switchMap(() =>
          this._waitForLayerViewUpdate('navigation').pipe(
            defaultIfEmpty(undefined)
          )
        ),
        // now actually do the hit-test
        switchMap(() => {
          const event = this._createClickEvent(latitude, longitude);
          return this._performHitTest(event, properties, options);
        }),
        take(1)
      )
      .subscribe({
        next: (result: FeatureDetail | null) => {
          if (result) {
            this.updateSelection(result);
          } else {
            this._logger.warn(
              '[SelectedItemService] No feature found for the given properties.'
            );
          }
        },
        error: (error) => {
          this._logger.error(
            '[SelectedItemService] Error during feature selection:',
            error
          );
        }
      });
  }

  /**
   * Waits for a specific layer view to complete its update process.
   *
   * @param layerId - The ID of the layer whose view update status is being monitored.
   * @returns An observable that emits `void` when the layer view has finished updating,
   *          or `null` if the layer view is not found or is not updating.
   */
  private _waitForLayerViewUpdate(layerId: string): Observable<void> {
    const layerView = this._api
      .getMapView()
      .allLayerViews.find((layerView) => layerView.layer.id === layerId);

    if (!layerView) {
      this._logger.warn(
        `[SelectedItemService] Layer view with ID '${layerId}' not found.`
      );
      return EMPTY;
    }

    if (!layerView.updating) {
      this._logger.trace(
        `[SelectedItemService] Layer view '${layerId}' is already updated.`
      );
      return EMPTY;
    }

    return new Observable<void>((observer) => {
      const handle = reactiveUtils.watch(
        () => layerView.updating,
        (isUpdating: boolean) => {
          if (isUpdating) {
            this._logger.trace(
              '[SelectedItemService] layerView is still updating...'
            );
          } else {
            this._logger.trace(
              `[SelectedItemService] Layer view '${layerId}' update complete.`
            );
            observer.next();
            observer.complete();
            handle.remove();
          }
        }
      );
    });
  }

  /**
   * Performs a hit test on the map view to identify features at the specified click event location.
   *
   * @param event - The view click event containing the map point and screen coordinates.
   * @param properties - The properties used to filter the hit test results, including attribute name and value.
   * @param options - Optional hit test options to include or exclude specific layers or graphics in the results.
   */
  private _performHitTest<T extends NavigationFeatureType>(
    event: ClickEvent,
    properties: ManualFeatureSelectionProperties<T>,
    options?: JepMapViewHitTestOptions
  ): Observable<FeatureDetail | null> {
    return from(this._api.getMapView().hitTest(event, options)).pipe(
      switchMap((response: ViewHitTestResult) => {
        return this._navHitTestSvc
          .manualResultHandler(
            response,
            properties.attributeName as string,
            properties.attributeValue
          )
          .pipe(
            take(1),
            catchError((error) => {
              this._logger.error(error);
              return of(error);
            })
          );
      }),
      take(1),
      catchError((error) => {
        this._logger.error(error);
        return of(error);
      })
    );
  }

  /**
   * Creates a click event based on the provided latitude and longitude.
   *
   * @param latitude - The latitude of the click event.
   * @param longitude - The longitude of the click event.
   * @returns A simulated view click event.
   */
  private _createClickEvent(
    latitude: number,
    longitude: number
  ): ClickEvent {
    const view = this._api.getMapView();
    const point = new Point({
      latitude,
      longitude,
      spatialReference: view.spatialReference
    });

    const screenPoint = view.toScreen(point);
    if (!screenPoint) {
      this._logger.error(
        '[SelectedItemService] (_createClickEvent) Failed to convert map point to screen point.'
      );
      throw new Error('Failed to convert map point to screen point.');
    }

    this._logger.trace(
      `[SelectedItemService] (_createClickEvent) screenPoint:`,
      screenPoint
    );

    const event = {
      mapPoint: point,
      x: screenPoint?.x,
      y: screenPoint?.y
    };
    this._logger.trace(
      `[SelectedItemService] (_createClickEvent) event:`,
      event
    );

    return event as unknown as ClickEvent;
  }

  /**
   * Clears the existing selection and removes the associated widget from the map.
   */
  clearExistingSelection() {
    const widget = this._api
      .getActiveWidgetMap()
      .get(this.currentSelection()?.navType as string);
    if (widget) {
      this._api.removeWidget(this.currentSelection()?.navType as string);
    }
    this.updateSelection(null);
  }

  /**
   * Adds a feature detail widget to the map for the specified selection.
   *
   * @param selection - The feature detail to be displayed in the widget.
   */
  private _addFeatureDetailWidget(selection: FeatureDetail): void {
    this._api.addWidget(selection.navType, {
      title: new TitleCasePipe().transform(selection.navType + 's'),
      pluginIDs: [],
      widgetComponent: NavWidgetComponent,
      data: selection
    });
  }

  /**
   * Cleans up resources when the service is destroyed, including removing the click handler.
   */
  ngOnDestroy(): void {
    if (this._clickHandle) {
      this._logger.trace(
        '[SelectedItemService] Click handler removed for 2D view (MapView)'
      );
      this._clickHandle.remove();
    }
  }
}
